import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  ChevronDown,
  CreditCard,
  Home,
  Building,
  DollarSign,
  Info,
  LogIn,
  Sparkles,
  Shield,
  Globe,
  Award,
  Users,
  TrendingUp,
  Banknote,
  Calculator,
  HeadphonesIcon,
  MapPin,
  Mail,
  Clock,
  Heart,
  Target,
  Briefcase
} from 'lucide-react';
import auroraMark from '../assets/aurora-mark.svg';

const BrandLogo = ({ className = '', dark = true }) => (
  <span className={`inline-flex items-center gap-2.5 sm:gap-3 ${className}`}>
    <img
      src={auroraMark}
      alt=""
      aria-hidden="true"
      className="h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12 flex-shrink-0"
    />
    <span className="flex flex-col leading-none">
      <span
        className={`font-sans font-extrabold tracking-wide text-[15px] sm:text-base md:text-lg ${
          dark ? 'text-[#0B1D36]' : 'text-white'
        }`}
      >
        AURORA WEALTH
      </span>
      <span
        className={`font-sans font-semibold tracking-[0.28em] text-[11px] sm:text-xs md:text-sm mt-1 ${
          dark ? 'text-[#0D9488]' : 'text-teal-200'
        }`}
      >
        RESERVES
      </span>
    </span>
  </span>
);

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDropdownOpen = (itemName, event) => {
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
    setActiveDropdown(itemName);
  };

  const navItems = [
    {
      name: 'Personal Banking',
      path: '/personal-banking',
      icon: Home,
      description: 'Personal financial solutions',
      subItems: [
        { 
          name: 'Checking Accounts', 
          path: '/personal-banking', 
          icon: CreditCard, 
          description: 'Everyday banking made simple' 
        },
        { 
          name: 'Savings Accounts', 
          path: '/personal-banking', 
          icon: TrendingUp, 
          description: 'Grow your savings with competitive rates' 
        },
        { 
          name: 'Credit Cards', 
          path: '/cards', 
          icon: CreditCard, 
          description: 'Rewards and cash back programs' 
        },
        { 
          name: 'Personal Loans', 
          path: '/loans', 
          icon: Banknote, 
          description: 'Quick and easy loan solutions' 
        },
        { 
          name: 'Investment Services', 
          path: '/personal-banking', 
          icon: TrendingUp, 
          description: 'Build wealth for your future' 
        }
      ]
    },
    {
      name: 'Business Banking',
      path: '/business-banking',
      icon: Building,
      description: 'Complete business solutions',
      subItems: [
        { 
          name: 'Business Checking', 
          path: '/business-banking', 
          icon: Building, 
          description: 'Professional banking for your business' 
        },
        { 
          name: 'Business Loans', 
          path: '/loans', 
          icon: Banknote, 
          description: 'Fuel your business growth' 
        },
        { 
          name: 'Merchant Services', 
          path: '/business-banking', 
          icon: CreditCard, 
          description: 'Accept payments seamlessly' 
        },
        { 
          name: 'Cash Management', 
          path: '/business-banking', 
          icon: Calculator, 
          description: 'Optimize your cash flow' 
        },
        { 
          name: 'Business Credit Cards', 
          path: '/cards', 
          icon: CreditCard, 
          description: 'Business rewards and benefits' 
        }
      ]
    },
    {
      name: 'Loans & Credit',
      path: '/loans',
      icon: DollarSign,
      description: 'Financing solutions for every need',
      subItems: [
        { 
          name: 'Home Mortgages', 
          path: '/loans', 
          icon: Home, 
          description: 'Make homeownership a reality' 
        },
        { 
          name: 'Auto Loans', 
          path: '/loans', 
          icon: DollarSign, 
          description: 'Drive away with great rates' 
        },
        { 
          name: 'Personal Loans', 
          path: '/loans', 
          icon: Banknote, 
          description: 'Quick approval, flexible terms' 
        },
        { 
          name: 'Business Loans', 
          path: '/loans', 
          icon: Building, 
          description: 'Capital for business expansion' 
        },
        { 
          name: 'Credit Cards', 
          path: '/cards', 
          icon: CreditCard, 
          description: 'Rewards and cashback options' 
        }
      ]
    },
    {
      name: 'Services',
      path: '/services',
      icon: Shield,
      description: 'Modern banking services',
      subItems: [
        { 
          name: 'Online Banking', 
          path: '/login', 
          icon: LogIn, 
          description: '24/7 account access' 
        },
        { 
          name: 'Bill Pay', 
          path: '/bill-pay', 
          icon: Calculator, 
          description: 'Pay bills online securely' 
        },
        { 
          name: 'Wire Transfers', 
          path: '/wire-transfer', 
          icon: TrendingUp, 
          description: 'Fast domestic and international transfers' 
        },
        { 
          name: 'Zelle Payments', 
          path: '/zelle', 
          icon: Mail, 
          description: 'Send money instantly' 
        },
        { 
          name: 'Customer Support', 
          path: '/support', 
          icon: HeadphonesIcon, 
          description: 'Expert help when you need it' 
        }
      ]
    },
    {
      name: 'About',
      path: '/about',
      icon: Info,
                      description: 'Learn about our company',
      subItems: [
        { 
          name: 'Our Story', 
          path: '/about', 
          icon: Heart, 
                          description: 'Private wealth banking since 1985' 
        },
        { 
          name: 'Leadership Team', 
          path: '/about', 
          icon: Users, 
          description: 'Meet our experienced leaders' 
        },
        { 
          name: 'Community Impact', 
          path: '/about', 
          icon: Target, 
          description: 'Supporting local communities' 
        },
        { 
          name: 'Careers', 
          path: '/about', 
          icon: Briefcase, 
          description: 'Join our growing team' 
        },
        { 
          name: 'Awards & Recognition', 
          path: '/about', 
          icon: Award, 
          description: 'Industry excellence' 
        }
      ]
    },
    {
      name: 'Contact',
      path: '/contact',
      icon: Mail,
      description: 'Get in touch with us',
      subItems: [
        { 
          name: 'Branch Locations', 
          path: '/contact', 
          icon: MapPin, 
          description: 'Find a branch near you' 
        },
        { 
          name: 'Contact Information', 
          path: '/contact', 
          icon: Mail,
          description: 'Email and online support' 
        },
        { 
          name: 'Customer Support', 
          path: '/support', 
          icon: HeadphonesIcon, 
          description: '24/7 assistance available' 
        },
        { 
          name: 'Schedule Appointment', 
          path: '/contact', 
          icon: Clock, 
          description: 'Meet with a banker' 
        }
      ]
    }
  ];

  const MobileMenu = () => (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9997] lg:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, type: 'spring', damping: 25 }}
            className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-[9998] lg:hidden overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <BrandLogo />
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation */}
            <div className="p-6 space-y-2">
              {navItems && navItems.map((item) => (
                <div key={item.name} className="border-b border-gray-100 last:border-b-0 pb-4 last:pb-0">
                  <button
                    onClick={() => setActiveMobileDropdown(
                      activeMobileDropdown === item.name ? null : item.name
                    )}
                    className="w-full flex items-center justify-between py-3 text-left group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary-50 rounded-lg text-primary-600 group-hover:bg-primary-100 transition-colors">
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-500">{item.description}</div>
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        activeMobileDropdown === item.name ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  <AnimatePresence>
                    {activeMobileDropdown === item.name && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-3 space-y-2 overflow-hidden"
                      >
                        {item.subItems && item.subItems.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-primary-50 transition-colors group"
                            onClick={() => {
                              setIsOpen(false);
                              setActiveMobileDropdown(null);
                            }}
                          >
                            <subItem.icon className="w-4 h-4 text-gray-400 group-hover:text-primary-600" />
                            <div>
                              <div className="font-medium text-gray-900 group-hover:text-primary-700">
                                {subItem.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {subItem.description}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="p-6 border-t border-gray-200">
              <Link
                to="/login"
                className="flex items-center justify-center space-x-2 w-full py-4 px-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg"
                onClick={() => setIsOpen(false)}
              >
                <LogIn className="w-5 h-5" />
                <span className="font-semibold">Online Banking</span>
              </Link>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-xl border-b border-gray-200/50'
            : 'bg-white/90 backdrop-blur-lg shadow-lg'
        }`}
        style={{ overflow: 'visible' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ overflow: 'visible' }}>
          <div className="flex items-center justify-between h-20" style={{ overflow: 'visible' }}>
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" aria-label="Aurora Wealth Reserves home">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="flex items-center"
              >
                <BrandLogo />
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" style={{ overflow: 'visible' }}>
              {navItems && navItems.map((item) => (
                <div
                  key={item.name}
                  className="relative group"
                  style={{ overflow: 'visible' }}
                  onMouseEnter={() => setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.path}
                    className="flex items-center space-x-1 px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors rounded-lg hover:bg-primary-50"
                  >
                    <span>{item.name}</span>
                    <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                  </Link>

                  {/* Desktop Dropdown - Fixed positioning and ultra-high z-index */}
                  <AnimatePresence>
                    {activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
                        style={{ 
                          zIndex: 99999,
                          position: 'absolute'
                        }}
                      >
                        <div className="p-2">
                          {item.subItems && item.subItems.map((subItem, index) => (
                            <Link
                              key={subItem.name}
                              to={subItem.path}
                              className="flex items-center space-x-3 p-3 rounded-xl hover:bg-primary-50 transition-colors group"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                                <subItem.icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 group-hover:text-primary-700">
                                  {subItem.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {subItem.description}
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/login"
                className="flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-3 rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all font-semibold shadow-lg hover:shadow-xl"
              >
                <LogIn className="w-5 h-5" />
                <span>Online Banking</span>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              onClick={() => setIsOpen(!isOpen)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu />
    </>
  );
};

export default Header; 